import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, RouterOutlet } from '@angular/router';
import { Common } from '@hsi/ui-core';
import { VideoPlaylist } from '@hsi/video-playlist';
import { environment } from '../../../environments/environment';
import { AppSharedService } from '../../services/app-shared.service';
import { AppUtilityService } from '../../services/app-utility.service';
import { AppStoreService } from '../../services/app-store.service';
import { AppSettingsService } from '../../services/app-settings.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-directory',
    templateUrl: './directory.component.html',
    styleUrls: ['./directory.component.scss'],
    imports: [CommonModule, FormsModule]
})
export class DirectoryComponent implements OnInit, OnDestroy {
  userName!: string;
  siteName!: string;
  selectedController: any = null;
  playlistControllersSorted: VideoPlaylist.Core.Services.PlaylistController[] = [];
  playlistControllers: VideoPlaylist.Core.Services.PlaylistController[] = [];
  playlistService!: VideoPlaylist.Core.Services.PlaylistService;
  dataSynchronizationService!: VideoPlaylist.Core.Services.DataSynchronizationService;
  offlineMode = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private appShared: AppSharedService,
    private appStore: AppStoreService,
    private appUtility: AppUtilityService,
    private appSettings: AppSettingsService
  ) {
    const authenticatedUser = this.appStore.authenticationService.authenticatedUser;
    if (!authenticatedUser) {
      this.router.navigate(['/']);
      return;
    }
    this.userName = authenticatedUser.name;
    this.siteName = authenticatedUser.customerName;

    const trainingResourceMicroservice = new VideoPlaylist.Infrastructure.Repositories.TrainingResourceMicroservice(
      this.appStore.microserviceHttpClient,
      environment.microservice.trainingResources
    );
    const videoPlaylistClientRepository = new VideoPlaylist.Infrastructure.Repositories.VideoPlaylistIndexedDbClientRepository();
    this.playlistService = new VideoPlaylist.Core.Services.PlaylistService(
      trainingResourceMicroservice,
      videoPlaylistClientRepository
    );
    this.dataSynchronizationService = new VideoPlaylist.Core.Services.DataSynchronizationService(
      videoPlaylistClientRepository
    );
  }

  ngOnInit(): void {
    if (!this.appShared.controllerStore.value.length) {
      this.appUtility.showProgressBar();
      if (!this.appShared.playlistStore.value.length) {
        this.playlistService.get()
          .then(playlists => this.initializePlaylistControllers(playlists))
          .catch(exception => {
            if (exception === 'Unauthorized') {
              this.appShared.logout(this.appStore.authenticationService);
            }
          });
      } else {
        this.initializePlaylistControllers(this.appShared.playlistStore.value);
      }
    }

    window.addEventListener('syncUpdate', this.syncUpdateHandler);
    this.appSettings.offlineMode$.subscribe(mode => {
      this.offlineMode = mode;
      this.setControllerFilter();
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('syncUpdate', this.syncUpdateHandler);
  }

  private syncUpdateHandler = () => {
    for (let i = 0; i < this.playlistControllers.length; i++) {
      this.playlistControllers[i].synchronizeWithClient();
    }
  };

  setControllerFilter(): void {
    this.playlistControllers = [];
    this.appShared.controllerStore.value
      .filter(controller => controller.synchronizationStatus.percentComplete === 100 || !this.offlineMode)
      .forEach(controller => this.playlistControllers.push(controller));
    this.playlistControllersSorted = [...this.playlistControllers].sort((x, y) =>
      x.playlist.title > y.playlist.title ? 1 : x.playlist.title < y.playlist.title ? -1 : 0
    );
  }

  async initializePlaylistControllers(playlists: VideoPlaylist.Core.Interfaces.Playlist[]): Promise<void> {
    this.appShared.playlistStore.next(
      playlists.sort((x, y) => x.title > y.title ? 1 : x.title < y.title ? -1 : 0)
    );
    const controllers: VideoPlaylist.Core.Services.PlaylistController[] = playlists.map(playlist =>
      new VideoPlaylist.Core.Services.PlaylistController(
        playlist,
        this.dataSynchronizationService,
        this.playlistService,
        this.appUtility.resolveVideoQuality()
      )
    );
    this.appUtility.showProgressBar();
    await Promise.all(controllers.map(c => c.synchronizeWithClient()));
    controllers.forEach(controller => {
      this.appShared.controllerStore.next([...this.appShared.controllerStore.value, controller]);
    });
    this.setControllerFilter();
    this.appUtility.hideProgressBar();
  }

  navigateToPlaylist(playlistId: string): void {
    this.router.navigate(['/', this.appShared.tennant, 'playlist', playlistId]);
  }

  async deletePlaylist(controller: VideoPlaylist.Core.Services.PlaylistController): Promise<void> {
    if (confirm(`This action will delete all downloaded assets from this playlist. Please click ok to continue.`)) {
      const name = controller.playlist.title;
      await controller.deletePlaylist();
      this.appUtility.displayMessage({
        text: `${name} has been removed successfully.`,
        actionText: "OK",
        duration: 4000
      });
    }
  }

  async syncPlaylist(controller: VideoPlaylist.Core.Services.PlaylistController): Promise<void> {
    const onCommit = () => {
      controller.downloadPlaylist(() =>
        this.appUtility.displayMessage({
          text: `${controller.playlist.title} has been downloaded successfully.`,
          actionText: "OK",
          duration: 4000
        })
      );
    };
    if (!controller.storageRequirements.storageEstimate.quota) {
      if (controller.storageRequirements.storageRequired > 0 &&
        confirm(`The minimum required space needed to perform this operation is ${Common.Core.Utilities.formatFileSize(controller.storageRequirements.storageRequired)}.  Please click ok to continue.`)
      ) {
        onCommit();
      }
    } else {
      const availableSpace = (controller.storageRequirements.storageEstimate.quota || 0) -
        (controller.storageRequirements.storageEstimate.usage || 0);
      if (availableSpace < controller.storageRequirements.storageRequired) {
        alert(`The minimum required space needed to perform this operation is ${Common.Core.Utilities.formatFileSize(controller.storageRequirements.storageRequired)} and the amount of space available is ${Common.Core.Utilities.formatFileSize(availableSpace)}.`);
      } else if (controller.storageRequirements.storageRequired > 0 &&
        confirm(`The minimum required space needed to perform this operation is ${Common.Core.Utilities.formatFileSize(controller.storageRequirements.storageRequired)}.  Please click ok to continue. (space used ${Common.Core.Utilities.formatFileSize(controller.storageRequirements.storageEstimate.usage)})`)
      ) {
        onCommit();
      }
    }
  }

  parseSsl(url: string): string {
    return url.replace('http://embed.wistia.com', 'https://embed-ssl.wistia.com')
      .replace('https://embed.wistia.com', 'https://embed-ssl.wistia.com');
  }
}